import React, { useState } from 'react';
import RichTextToolbar from './RichTextToolbar';

const RichTextEditor = ({
  section,
  index,
  onUpdate,
  onUpdateStyling,
  onRemove,
  onMoveUp,
  onMoveDown
}) => {
  const [showStyling, setShowStyling] = useState(false);

  const fontSizeOptions = [
    { value: 'xs', label: 'Extra Small' },
    { value: 'sm', label: 'Small' },
    { value: 'base', label: 'Normal' },
    { value: 'lg', label: 'Large' },
    { value: 'xl', label: 'Extra Large' },
    { value: '2xl', label: '2XL' },
    { value: '3xl', label: '3XL' }
  ];

  const paddingOptions = [
    { value: 'none', label: 'None' },
    { value: 'small', label: 'Small' },
    { value: 'normal', label: 'Normal' },
    { value: 'large', label: 'Large' },
    { value: 'xl', label: 'Extra Large' }
  ];

  const borderRadiusOptions = [
    { value: 'none', label: 'None' },
    { value: 'sm', label: 'Small' },
    { value: 'md', label: 'Medium' },
    { value: 'lg', label: 'Large' },
    { value: 'xl', label: 'Extra Large' },
    { value: 'full', label: 'Full' }
  ];

  const borderOptions = [
    { value: 'none', label: 'None' },
    { value: 'thin', label: 'Thin' },
    { value: 'normal', label: 'Normal' },
    { value: 'thick', label: 'Thick' }
  ];

  const positionOptions = [
    { value: 'after-intro', label: 'After Intro' },
    { value: 'before-process', label: 'Before Process' },
    { value: 'after-process', label: 'After Process' },
    { value: 'before-cta', label: 'Before Call to Action' }
  ];

  const colorOptions = [
    { value: '#374151', label: 'Gray' },
    { value: '#1f2937', label: 'Dark Gray' },
    { value: '#6b7280', label: 'Light Gray' },
    { value: '#dc2626', label: 'Red' },
    { value: '#ea580c', label: 'Orange' },
    { value: '#d97706', label: 'Amber' },
    { value: '#65a30d', label: 'Lime' },
    { value: '#16a34a', label: 'Green' },
    { value: '#0d9488', label: 'Teal' },
    { value: '#0891b2', label: 'Cyan' },
    { value: '#2563eb', label: 'Blue' },
    { value: '#4f46e5', label: 'Indigo' },
    { value: '#7c3aed', label: 'Violet' },
    { value: '#9333ea', label: 'Purple' },
    { value: '#ec4899', label: 'Pink' },
    { value: '#f43f5e', label: 'Rose' }
  ];

  const backgroundColorOptions = [
    { value: 'transparent', label: 'Transparent' },
    { value: '#f9fafb', label: 'Light Gray' },
    { value: '#f3f4f6', label: 'Gray' },
    { value: '#fef2f2', label: 'Light Red' },
    { value: '#fffbeb', label: 'Light Amber' },
    { value: '#f0fdf4', label: 'Light Green' },
    { value: '#ecfdf5', label: 'Light Emerald' },
    { value: '#f0f9ff', label: 'Light Blue' },
    { value: '#faf5ff', label: 'Light Purple' },
    { value: '#fdf2f8', label: 'Light Pink' }
  ];

  const getPaddingClass = (padding) => {
    switch (padding) {
      case 'none':
        return 'p-0';
      case 'small':
        return 'p-3';
      case 'normal':
        return 'p-6';
      case 'large':
        return 'p-8';
      case 'xl':
        return 'p-12';
      default:
        return 'p-6';
    }
  };

  const getBorderRadiusClass = (radius) => {
    switch (radius) {
      case 'none':
        return 'rounded-none';
      case 'sm':
        return 'rounded-sm';
      case 'md':
        return 'rounded-md';
      case 'lg':
        return 'rounded-lg';
      case 'xl':
        return 'rounded-xl';
      case 'full':
        return 'rounded-full';
      default:
        return 'rounded-none';
    }
  };

  const getBorderClass = (border) => {
    switch (border) {
      case 'none':
        return 'border-0';
      case 'thin':
        return 'border';
      case 'normal':
        return 'border-2';
      case 'thick':
        return 'border-4';
      default:
        return 'border-0';
    }
  };

  const getFontSizeClass = (size) => {
    switch (size) {
      case 'xs':
        return 'text-xs';
      case 'sm':
        return 'text-sm';
      case 'base':
        return 'text-base';
      case 'lg':
        return 'text-lg';
      case 'xl':
        return 'text-xl';
      case '2xl':
        return 'text-2xl';
      case '3xl':
        return 'text-3xl';
      default:
        return 'text-base';
    }
  };

  return (
    <div className="mb-8 p-6 border border-gray-200 rounded-lg bg-gradient-to-r from-indigo-50 to-purple-50">
      {/* Section Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <h4 className="text-xl font-semibold text-gray-900">
            Rich Content Section {index + 1}
          </h4>
          <div className="flex items-center space-x-2">
            <button
              type="button"
              onClick={() => onMoveUp(index)}
              disabled={index === 0}
              className="p-1 text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Move Up">
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 15l7-7 7 7"
                />
              </svg>
            </button>
            <button
              type="button"
              onClick={() => onMoveDown(index)}
              className="p-1 text-gray-500 hover:text-gray-700"
              title="Move Down">
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <button
            type="button"
            onClick={() => setShowStyling(!showStyling)}
            className="px-3 py-1 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium">
            {showStyling ? 'Hide Styling' : 'Show Styling'}
          </button>
          <button
            type="button"
            onClick={() => onRemove(index)}
            className="px-3 py-1 text-sm bg-red-600 hover:bg-red-700 text-white rounded-md font-medium">
            Remove Section
          </button>
        </div>
      </div>

      {/* Position Selector */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Section Position
        </label>
        <select
          value={section.position}
          onChange={(e) => onUpdate(index, 'position', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500">
          {positionOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <p className="mt-1 text-sm text-gray-500">
          Choose where this section appears in the blog article
        </p>
      </div>

      {/* Title */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Section Title (Optional)
        </label>
        <input
          type="text"
          value={section.title}
          onChange={(e) => onUpdate(index, 'title', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          placeholder="Enter section title..."
        />
      </div>

      {/* Content */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Content
        </label>
        <RichTextToolbar
          value={section.content}
          onChange={(value) => onUpdate(index, 'content', value)}
          placeholder="Write your content here... Use the toolbar above to format your text, or select text and click formatting buttons to apply styles."
        />
        <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
          <h6 className="text-sm font-medium text-blue-900 mb-2">
            💡 Pro Tips:
          </h6>
          <ul className="text-xs text-blue-700 space-y-1">
            <li>
              • <strong>Select text</strong> then click formatting buttons to
              apply styles
            </li>
            <li>
              • Use <strong>keyboard shortcuts</strong>: Ctrl+B (bold), Ctrl+I
              (italic), Ctrl+U (underline), Ctrl+K (link)
            </li>
            <li>
              • <strong>Convert text to lists</strong> by selecting multiple
              lines and clicking list buttons
            </li>
            <li>
              • <strong>Insert tables, quotes, and code</strong> using the
              special element buttons
            </li>
            <li>
              • <strong>Color your text</strong> with the color picker for
              emphasis
            </li>
          </ul>
        </div>
      </div>

      {/* Styling Options */}
      {showStyling && (
        <div className="border-t border-gray-200 pt-6">
          <h5 className="text-lg font-medium text-gray-900 mb-4">
            Styling Options
          </h5>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Background Color */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Background Color
              </label>
              <select
                value={section.styling.backgroundColor}
                onChange={(e) =>
                  onUpdateStyling(index, 'backgroundColor', e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500">
                {backgroundColorOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Text Color */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Text Color
              </label>
              <select
                value={section.styling.textColor}
                onChange={(e) =>
                  onUpdateStyling(index, 'textColor', e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500">
                {colorOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Font Size */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Font Size
              </label>
              <select
                value={section.styling.fontSize}
                onChange={(e) =>
                  onUpdateStyling(index, 'fontSize', e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500">
                {fontSizeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Padding */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Padding
              </label>
              <select
                value={section.styling.padding}
                onChange={(e) =>
                  onUpdateStyling(index, 'padding', e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500">
                {paddingOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Border Radius */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Border Radius
              </label>
              <select
                value={section.styling.borderRadius}
                onChange={(e) =>
                  onUpdateStyling(index, 'borderRadius', e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500">
                {borderRadiusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Border */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Border
              </label>
              <select
                value={section.styling.border}
                onChange={(e) =>
                  onUpdateStyling(index, 'border', e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500">
                {borderOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Preview */}
          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Live Preview
            </label>
            <div
              className={`${getPaddingClass(
                section.styling.padding
              )} ${getBorderRadiusClass(
                section.styling.borderRadius
              )} ${getBorderClass(section.styling.border)} ${getFontSizeClass(
                section.styling.fontSize
              )} min-h-[200px]`}
              style={{
                backgroundColor:
                  section.styling.backgroundColor === 'transparent'
                    ? 'transparent'
                    : section.styling.backgroundColor,
                color: section.styling.textColor,
                borderColor:
                  section.styling.border !== 'none' ? '#d1d5db' : 'transparent'
              }}>
              {section.title && (
                <h3
                  className="text-xl font-semibold mb-3"
                  dangerouslySetInnerHTML={{ __html: section.title }}
                />
              )}
              <div
                className="prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{
                  __html:
                    section.content ||
                    '<p class="text-gray-400 italic">Preview of your content will appear here...</p>'
                }}
              />
            </div>
            {section.content && (
              <div className="mt-2 text-xs text-gray-500">
                <span className="font-medium">Content Stats:</span>{' '}
                {section.content.length} characters,{' '}
                {section.content.split(' ').length} words
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default RichTextEditor;
